import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type DistinctHierarchiesResponse, type DistinctHierarchy } from '@sr/types';
import { getApiUrl } from '#lib/utils/environment';
import { WithCustomerPortalApi } from '../customerPortalApi';

const STALE_TIME = 10 * 60 * 1000;

// Module-scope so react-query memoizes the select result; an inline arrow
// would produce a new array identity on every render.
const selectHierarchies = (data: DistinctHierarchiesResponse): DistinctHierarchy[] =>
  data.availableHierarchies;

type Props = {
  dwKeys: string[];
  enabled: boolean;
};

const useDistinctHierarchyApi = WithCustomerPortalApi<
  DistinctHierarchiesResponse,
  UseQueryResult<DistinctHierarchy[]>,
  Props
>(
  (fetchFn, { dwKeys, enabled }) => {
    return useQuery({
      queryKey: ['distinct-hierarchies', dwKeys],
      enabled: enabled && dwKeys.length > 0,
      staleTime: STALE_TIME,
      queryFn: () =>
        fetchFn({
          url: `${getApiUrl()}/partners/hierarchies/distinct`,
          options: { method: 'POST', body: JSON.stringify({ dwKeys }) },
        }),
      select: selectHierarchies,
    });
  },
  { hookName: 'useDistinctHierarchy' },
);

export const useDistinctHierarchy = (
  dwKeys: string[],
  enabled = true,
): UseQueryResult<DistinctHierarchy[]> => useDistinctHierarchyApi({ dwKeys, enabled });
